import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom'
import AuthCallbackPage from './routes/AuthCallbackPage'
import AuthGuard from './components/AuthGuard'
import useAuth from './models/useAuth'

import OnboardWelcome from './components/onboard/Welcome'
import OnboardNetwork from './components/onboard/Network'
import OnboardNode from './components/onboard/Node'
import OnboardLLM from './components/onboard/LLM'
import Onboard from './components/onboard'
import ModalLLM from './components/ModalLLM'
import ModalNetwork from './components/ModalNetwork'
import ModalNode from './components/ModalNode'
import ModalComputeNode from './components/ModalComputeNode'
import ModalService from './components/ModalService'
import Permissions from './components/Permissions'
import Members from './components/Members'
import OrgSSO from './components/OrgSSO'
import Usage from './components/Usage'
import Billing from './components/Billing'
import Audit from './components/Audit'
import InviteAccept from './components/InviteAccept'
import Shared from './components/Shared'
import ModalFeedback from './components/ModalFeedback'
import StandaloneTerminalLayout from './components/StandaloneTerminalLayout'
import AccessRequest from './components/AccessRequest'
import Networks from './components/Networks'
import Network from './components/Network'
import Account from './components/Account'
import HeaderLayout from './components/HeaderLayout'
import AppLayout from './components/AppLayout'
import SignIn from './components/SignIn'
import Register from './components/Register'
import CliCode from './components/CliCode'
import Token from './components/Token'
import Nodes from './components/Nodes'
import Node from './components/Node'
import NodeFiles from './components/NodeFiles'
import NetworkFiles from './components/NetworkFiles'

/**
 * `/register`: the approval form for a session the code already found (`?session=`, the page's own
 * address after the code step), and the code step otherwise (nobgp-router#1859).
 */
function RegisterEntry() {
  const [searchParams] = useSearchParams()
  return searchParams.has('session') ? <Register /> : <CliCode kind="register" />
}

/** The old form of a node page, `/nodes/<node>/<network>`: the node's one page (app#498). */
function OldNodePath() {
  const { nodeId } = useParams()
  return <Navigate to={`/nodes/${nodeId}`} replace />
}

export default function App() {
  const { user } = useAuth()

  return (
    <Routes>
      <Route element={<HeaderLayout />}>
        <Route path="/login" element={<SignIn />} />
        {/* The code step of `nobgp login` and `nobgp register` (nobgp-router#1859): one page. It
            sends a reader with no session to /login and back itself. ⚠ A login code is typed at
            /activate, never /login: /login is the app's own sign-in page. */}
        <Route path="/activate" element={<CliCode kind="login" />} />
        <Route path="/activate/:code" element={<CliCode kind="login" />} />
        <Route path="/register/:code" element={<CliCode kind="register" />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* The browser leg of a node registration (nobgp-router#1603, #1859): the code step, then
            the approval form at `?session=`. Outside AuthGuard on purpose: each sends a reader
            with no session to sign in itself. */}
        <Route path="/register" element={<RegisterEntry />} />
      </Route>
      <Route element={<AuthGuard />}>
        <Route path="terminal/:serviceName" element={<StandaloneTerminalLayout />} />
        <Route element={<HeaderLayout />}>
          <Route path="access/:serviceName" element={<AccessRequest />} />
          <Route path="invite/:token" element={<InviteAccept />} />
          <Route element={<AppLayout />}>
            <Route path="onboard" element={<Onboard />}>
              <Route path="welcome" element={<OnboardWelcome />} />
              <Route path="network" element={<OnboardNetwork />} />
              <Route path="node" element={<OnboardNode />} />
              <Route path="llm" element={<OnboardLLM />} />
            </Route>
            <Route path="llm" element={<ModalLLM />} />
            <Route path="node/permissions/:nodeId" element={<Permissions />} />
            <Route path="node/:networkId?" element={<ModalNode />} />
            <Route path="compute/:networkId?" element={<ModalComputeNode />} />
            <Route path="service/:nodeId/:serviceId?" element={<ModalService />} />
            <Route path="permissions/:serviceName?" element={<Permissions />} />
            <Route path="org/members" element={<Members />} />
            <Route path="org/sso" element={<OrgSSO />} />
            <Route path="usage" element={<Usage />} />
            <Route path="billing" element={<Billing />} />
            <Route path="account/audit" element={<Audit />} />
            <Route path="shared" element={<Shared />} />
            <Route path="network/:networkId?" element={<ModalNetwork />} />
            <Route path="feedback" element={<ModalFeedback />} />
            <Route path="account/:accountId" element={<Account />} />
            <Route path="account" element={<Navigate to={`/account/${user?.id}`} replace />} />
            <Route path="networks" element={<Networks />} />
            <Route path="networks/:networkId" element={<Network />} />
            <Route path="networks/:networkId/files/*" element={<NetworkFiles />} />
            <Route path="networks/token/:tokenId?" element={<Token />} />
            <Route path="nodes" element={<Nodes />} />
            <Route path="nodes/:nodeId/files/*" element={<NodeFiles />} />
            {/* A node has one page, whatever networks it is in (app#498). The old form holds a
                network id: a mailed link or a bookmark still opens the node. */}
            <Route path="nodes/:nodeId/:networkId" element={<OldNodePath />} />
            <Route path="nodes/:nodeId" element={<Node />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}
